import { typedSupabase } from "../lib/typed-supabase";
import type { IService, ServiceResponse } from "./service-registry";
import { createServiceError} from "./service-registry";
import { Category, CreateCategoryRequest, UpdateCategoryRequest, } from "../types/category";
import { logger } from "../lib/logger";
// ============================================================================
// CATEGORY SERVICE - Phase 4 Architecture
// ============================================================================
export interface ICategoryService extends IService {
    getCategories(): Promise<ServiceResponse<Category[]>>;
    createCategory(categoryData: CreateCategoryRequest): Promise<ServiceResponse<Category>>;
    updateCategory(id: string, updates: UpdateCategoryRequest): Promise<ServiceResponse<Category>>;
    deleteCategory(id: string): Promise<ServiceResponse<void>>;
    findCategoryByName(name: string): Promise<ServiceResponse<Category | undefined>>;
}
export class CategoryService implements ICategoryService {
    readonly name = "CategoryService";
    private readonly TABLE_NAME = "categories" as const;
    async initialize(): Promise<void> {
        logger.info("CategoryService initialized", { service: this.name });
    }
    async dispose(): Promise<void> {
        logger.info("CategoryService disposed", { service: this.name });
    }
    /**
     * Fetches all categories from the database
     */
    async getCategories(): Promise<ServiceResponse<Category[]>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from(this.TABLE_NAME)
                .select("*")
                .order("name", { ascending: true });
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            return { success: true, data: data as Category[] };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    /**
     * Creates a new category
     */
    async createCategory(categoryData: CreateCategoryRequest): Promise<ServiceResponse<Category>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from(this.TABLE_NAME)
                .insert([categoryData])
                .select()
                .single();
            if (error) {
                return { success: false, error: error.message, code: "CREATE_FAILED", service: this.name };
            }
            return { success: true, data: data as Category };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "CREATE_FAILED", service: this.name };
        }
    }
    /**
     * Updates an existing category
     */
    async updateCategory(id: string, updates: UpdateCategoryRequest): Promise<ServiceResponse<Category>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from(this.TABLE_NAME)
                .update(updates)
                .eq("id", id)
                .select()
                .single();
            if (error) {
                return { success: false, error: error.message, code: "UPDATE_FAILED", service: this.name };
            }
            return { success: true, data: data as Category };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "UPDATE_FAILED", service: this.name };
        }
    }
    /**
     * Deletes a category
     */
    async deleteCategory(id: string): Promise<ServiceResponse<void>> {
        const startTime = Date.now();
        try {
            const { error } = await typedSupabase
                .from(this.TABLE_NAME)
                .delete()
                .eq("id", id);
            if (error) {
                return { success: false, error: error.message, code: "DELETE_FAILED", service: this.name };
            }
            return { success: true, data: undefined };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "DELETE_FAILED", service: this.name };
        }
    }
    /**
     * Finds a category by name
     */
    async findCategoryByName(name: string): Promise<ServiceResponse<Category | undefined>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from(this.TABLE_NAME)
                .select("*")
                .eq("name", name)
                .single();
            // Handle "No rows found" as a valid undefined result, not an error
            if (error && error.code === "PGRST116") {
                return { success: true, data: undefined };
            }
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            return { success: true, data: data as Category };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
}
// ============================================================================
// SINGLETON EXPORT - Following architectural consolidation plan
// ============================================================================
export const categoryService = new CategoryService();
